import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { token } from '@atlaskit/tokens';
import { defineMessages, useIntl } from '@atlassian/jira-intl';
import { useAutobatch } from '@atlassian/jira-software-react-scheduler/src/ui/autobatch/index.tsx';
import { layout, minCardHeight } from '../../../../common/constants/styles/index.tsx';

const hoverIntentDelayMs = 100;

const messages = defineMessages({
	transitionTo: {
		id: 'platform-board-kit.column.column-transition-zones-container.transition-to-zone.transition-to',
		defaultMessage: 'Transition To...',
		description: 'Menu label that reveals possible status transitions once hovered over',
	},
});

type Props = {
	onHover: () => void;
};

const ColumnTransitionToZone = ({ onHover }: Props) => {
	const { formatMessage } = useIntl();
	const autobatch = useAutobatch();
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const batchSetIsDraggingOver = useCallback(
		(draggingOver: boolean) => {
			autobatch(() => setIsDraggingOver(draggingOver));
		},
		[autobatch],
	);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const previousInputPosition = useRef<{ x: number; y: number } | null>(null);
	const debouncedHoverIntent = useMemo(() => debounce(onHover, hoverIntentDelayMs), [onHover]);

	useEffect(() => {
		if (!containerRef.current) return undefined;

		return dropTargetForElements({
			element: containerRef.current,
			onDragEnter: () => {
				batchSetIsDraggingOver(true);
			},
			onDrag: ({
				location: {
					current: { input },
				},
			}) => {
				if (
					previousInputPosition.current?.x !== input.clientX ||
					previousInputPosition.current?.y !== input.clientY
				) {
					debouncedHoverIntent();
					previousInputPosition.current = {
						x: input.clientX,
						y: input.clientY,
					};
				}
			},
			onDragLeave: (_args) => {
				debouncedHoverIntent.cancel();
				batchSetIsDraggingOver(false);
			},
		});
	}, [batchSetIsDraggingOver, debouncedHoverIntent]);

	return (
		<TransitionToZone
			isDraggingOver={isDraggingOver}
			ref={containerRef}
			data-component-selector="platform-board-kit.ui.column.column-transition-zones-container.transition-to-zone.transition-to-zone"
			data-testid="platform-board-kit.ui.column.column-transition-zones-container.transition-to-zone"
		>
			{formatMessage(messages.transitionTo)}
		</TransitionToZone>
	);
};

export default ColumnTransitionToZone;

const ttZoneHeight = minCardHeight * 0.5 - 5;
const ttZoneMargin = layout.adg3smallGutter * 0.5;
const ttZoneMarginTop = -(ttZoneHeight + layout.adg3smallGutter);

type TransitionToZoneProps = {
	isDraggingOver?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TransitionToZone = styled.div<TransitionToZoneProps>({
	position: 'sticky',
	textAlign: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	pointerEvents: 'all !important',
	top: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `${ttZoneMarginTop}px ${ttZoneMargin}px ${ttZoneMargin}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: (props: TransitionToZoneProps) =>
		props.isDraggingOver
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`2px solid ${token('color.border.brand')}`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`2px solid ${token('color.border.brand')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: TransitionToZoneProps) =>
		props.isDraggingOver
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.selected.hovered')
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.background.selected'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ttZoneHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography -- Ignored via go/DSP-18766
	lineHeight: `${ttZoneHeight}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `calc(100% - ${layout.adg3smallGutter * 1.5}px)`,
	zIndex: 10,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${layout.cardListRadius}px`,
});
